// CartModal — multi-item checkout.
//
// Shows every line in the cart (editable qty + variant, removable),
// one shared shipping address (reusing OrderModal's address-book
// picker), one facilitation choice, and a combined total. Submitting
// posts the whole cart to /api/orders/cart, which creates N orders
// that share ONE order number + order group (migration 0074).
//
// The address / reseller-customer / facilitation machinery is a
// deliberate mirror of OrderModal — a cart is "the same checkout, many
// items". AddressList + Hint are imported from OrderModal so the saved-
// address UI stays identical.
import { useState, useEffect, useMemo } from "react";
import { api } from "../api.js";
import { T, T_NEW } from "../theme.js";
import { Btn, Inp, Modal, Mono, Serif } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT } from "../i18n.jsx";
import { AddressList, Hint } from "./OrderModal.jsx";
import { useCart, cartSetQty, cartSetVariant, cartRemove, cartClear } from "../cart.js";

const LAST_SHIP_KEY = "cg_last_ship_v1";
function loadLastShip() {
  try {
    const v = JSON.parse(localStorage.getItem(LAST_SHIP_KEY) || "null");
    if (v && typeof v === "object") return v;
  } catch {}
  return {};
}
function saveLastShip(s) {
  try { localStorage.setItem(LAST_SHIP_KEY, JSON.stringify(s)); } catch {}
}

// Qty field with a local editing buffer so the user can clear it and
// type a fresh number. We only commit a valid (>=1) value to the cart;
// an empty/partial entry is tolerated while typing and clamped on blur.
function QtyInput({ value, onCommit }) {
  const [v, setV] = useState(String(value));
  useEffect(() => { setV(String(value)); }, [value]);
  return (
    <input
      type="number" min={1} max={1000} value={v}
      onFocus={(e) => e.target.select()}
      onChange={(e) => {
        const raw = e.target.value;
        setV(raw);
        const n = parseInt(raw, 10);
        if (Number.isFinite(n) && n >= 1) onCommit(Math.min(1000, n));
      }}
      onBlur={() => {
        const n = parseInt(v, 10);
        const clamped = Number.isFinite(n) ? Math.max(1, Math.min(1000, n)) : 1;
        setV(String(clamped));
        onCommit(clamped);
      }}
      style={{ width: 58, background: T.bg, color: T.text,
               border: `1px solid ${T.border}`, padding: "6px 8px",
               borderRadius: 8, fontSize: 13, textAlign: "center" }}
    />
  );
}

export function CartModal({ onClose, onDone }) {
  const t = useT();
  const toast = useToast();
  const items = useCart();
  const last = loadLastShip();

  const [notes, setNotes] = useState("");
  const [recipientName, setRecipientName] = useState(last.recipientName || "");
  const [recipientPhone, setRecipientPhone] = useState(last.recipientPhone || "");
  const [shipAddress, setShipAddress] = useState(last.shipAddress || "");
  const [shipCountry, setShipCountry] = useState(last.shipCountry || t("order.modal.countryDefault"));
  const [submitting, setSubmitting] = useState(false);
  const [facilitated, setFacilitated] = useState(true);

  const [myAddresses, setMyAddresses] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState(null);
  const [addrSource, setAddrSource] = useState("manual");
  const [selectedAddrId, setSelectedAddrId] = useState(null);
  const [resellerCustomerId, setResellerCustomerId] = useState("");
  const [myCustomers, setMyCustomers] = useState(null);
  // Reseller's own display FX rate (₽/$) for suggesting bookkeeping
  // prices on self-purchase, plus per-line manual overrides keyed by
  // postId|variant.
  const [resellerRate, setResellerRate] = useState(null);
  const [rprices, setRprices] = useState({});

  const lineKey = (it) => it.postId + "|" + (it.variant || "");
  const suggestRub = (it) =>
    resellerRate ? Math.round(((it.priceCents ?? 0) / 100) * it.qty * resellerRate) : null;
  const rubValue = (it) => {
    const k = lineKey(it);
    if (rprices[k] !== undefined) return rprices[k];
    const s = suggestRub(it);
    return s != null ? String(s) : "";
  };

  const applyAddress = (a) => {
    if (!a) return;
    setRecipientName(a.recipientName || "");
    setRecipientPhone(a.phone || "");
    setShipAddress(a.address || "");
    setShipCountry(a.country || t("order.modal.countryDefault"));
  };

  useEffect(() => {
    api.listMyCustomers({ limit: 200 }).then(setMyCustomers).catch(() => setMyCustomers([]));
    api.getResellerSettings()
      .then((s) => setResellerRate(s?.fxRateRubPerUsd ?? null))
      .catch(() => {});
    api.listMyAddresses()
      .then((rows) => {
        setMyAddresses(rows);
        if (rows && rows.length > 0) {
          const def = rows.find((a) => a.isDefault) || rows[0];
          setAddrSource("my");
          setSelectedAddrId(def.id);
          applyAddress(def);
        }
      })
      .catch(() => setMyAddresses([]));
  }, []);

  useEffect(() => {
    if (!resellerCustomerId) {
      setCustomerAddresses(null);
      if (addrSource === "customer") {
        setAddrSource(myAddresses && myAddresses.length > 0 ? "my" : "manual");
      }
      return;
    }
    api.listCustomerAddresses(resellerCustomerId)
      .then((rows) => {
        setCustomerAddresses(rows);
        if (addrSource === "customer" && rows.length > 0) {
          const prim = rows.find((a) => a.isPrimary) || rows[0];
          setSelectedAddrId(prim.id);
          applyAddress(prim);
        }
      })
      .catch(() => setCustomerAddresses([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resellerCustomerId]);

  const switchToMy = () => {
    setAddrSource("my");
    if (myAddresses && myAddresses.length > 0) {
      const def = myAddresses.find((a) => a.isDefault) || myAddresses[0];
      setSelectedAddrId(def.id);
      applyAddress(def);
    } else setSelectedAddrId(null);
  };
  const switchToCustomer = () => {
    setAddrSource("customer");
    if (customerAddresses && customerAddresses.length > 0) {
      const prim = customerAddresses.find((a) => a.isPrimary) || customerAddresses[0];
      setSelectedAddrId(prim.id);
      applyAddress(prim);
    } else setSelectedAddrId(null);
  };
  const switchToManual = () => { setAddrSource("manual"); setSelectedAddrId(null); };

  // Combined totals across all lines.
  const totals = useMemo(() => {
    let subUsd = 0, subRub = 0, feeUsd = 0, feeRub = 0, cashUsd = 0, cashRub = 0;
    for (const it of items) {
      const u = it.priceCents != null ? it.priceCents / 100 : 0;
      const r = it.priceRubCents != null ? it.priceRubCents / 100 : 0;
      const lineUsd = u * it.qty, lineRub = r * it.qty;
      subUsd += lineUsd; subRub += lineRub;
      const fp = facilitated ? (it.organizerFeePct || 0) : 0;
      feeUsd += lineUsd * fp / 100; feeRub += lineRub * fp / 100;
      const cp = facilitated ? (it.cashbackPct || 0) : 0;
      cashUsd += lineUsd * cp / 100; cashRub += lineRub * cp / 100;
    }
    return {
      subUsd, subRub, feeUsd, feeRub, cashUsd, cashRub,
      grandUsd: subUsd + feeUsd, grandRub: subRub + feeRub,
      hasFee: feeUsd > 0, hasCash: cashUsd > 0,
    };
  }, [items, facilitated]);

  const customerTabDisabled = !resellerCustomerId;
  const tabBtn = (active) => ({
    flex: 1, padding: "8px 10px",
    background: active ? T_NEW.accent : "transparent",
    color: active ? "#fff" : T_NEW.ink,
    border: `1px solid ${active ? T_NEW.accent : T_NEW.ink12}`,
    borderRadius: T_NEW.r1, cursor: "pointer",
    fontSize: 13, fontWeight: 600, fontFamily: T_NEW.fontSans,
  });

  const submit = async () => {
    if (items.length === 0) { toast.error(t("cart.empty")); return; }
    if (!recipientName.trim()) { toast.error(t("order.recipient.required")); return; }
    if (!shipAddress.trim()) { toast.error(t("order.address.required")); return; }
    setSubmitting(true);
    try {
      const res = await api.placeCartOrder({
        items: items.map((it) => {
          const base = {
            postId: it.postId,
            qty: it.qty,
            variant: it.variant?.trim() || undefined,
          };
          // On self-purchase, attach the reseller's ruble bookkeeping price.
          if (!facilitated) {
            const raw = rubValue(it);
            const cents = raw.trim() === "" ? null : Math.round(parseFloat(raw) * 100);
            if (cents != null && Number.isFinite(cents) && cents >= 0) {
              base.resellerPriceRubCents = cents;
            }
          }
          return base;
        }),
        notes: notes.trim() || undefined,
        recipientName: recipientName.trim(),
        recipientPhone: recipientPhone.trim() || undefined,
        shipAddress: shipAddress.trim(),
        shipCountry: shipCountry.trim() || undefined,
        resellerCustomerId: resellerCustomerId || undefined,
        facilitated,
      });
      saveLastShip({
        recipientName: recipientName.trim(),
        recipientPhone: recipientPhone.trim(),
        shipAddress: shipAddress.trim(),
        shipCountry: shipCountry.trim(),
      });
      const skippedN = Array.isArray(res?.skipped) ? res.skipped.length : 0;
      if (skippedN > 0) {
        toast.info(t("cart.placedSomeSkipped", { ok: res.count, skipped: skippedN, num: res.orderNumber }));
      } else {
        toast.success(t("cart.placed", { n: res.count, num: res.orderNumber }));
      }
      cartClear();
      // Navigate to orders on success if the caller wired onDone;
      // otherwise just close.
      if (onDone) onDone(); else onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <Modal title={t("cart.title")} onClose={onClose} width={460}>
        <div style={{ padding: "28px 8px", textAlign: "center" }}>
          <Serif size={T_NEW.bodyLg} color={T_NEW.ink56} style={{ display: "block" }}>
            {t("cart.emptyBody")}
          </Serif>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>{t("common.cancel")}</Btn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={t("cart.title")} onClose={onClose} width={460}>
      {/* Line items */}
      <div style={{ marginBottom: T_NEW.s5 }}>
        {items.map((it) => {
          const u = it.priceCents != null ? it.priceCents / 100 : null;
          return (
            <div key={it.postId + "|" + (it.variant || "")}
                 style={{ display: "flex", gap: 10, padding: "10px 0",
                          borderBottom: `1px solid ${T_NEW.ink12}` }}>
              {it.image && (
                <img src={it.image} alt="" style={{ width: 52, height: 52, objectFit: "cover",
                                                     borderRadius: T_NEW.r1, flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <Serif size={T_NEW.body} style={{ display: "block", overflow: "hidden",
                       textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {it.title}
                </Serif>
                {u != null && (
                  <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginTop: 2 }}>
                    ${u.toFixed(2)} · {t("cart.each")}
                  </Mono>
                )}
                <input
                  value={it.variant || ""}
                  onChange={(e) => cartSetVariant(it.postId, it.variant, e.target.value)}
                  placeholder={t("order.variant.placeholder")}
                  style={{ width: "100%", marginTop: 6, background: T.bg, color: T.text,
                           border: `1px solid ${T.border}`, padding: "5px 8px",
                           borderRadius: 8, fontSize: 12.5, fontFamily: "inherit" }}
                />
                {/* Self-purchase bookkeeping price (₽). FX-suggested, editable. */}
                {!facilitated && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                    <span style={{ fontSize: 13, color: T_NEW.ink56, fontFamily: T_NEW.fontMono }}>₽</span>
                    <input
                      value={rubValue(it)}
                      inputMode="numeric"
                      onChange={(e) => setRprices((p) => ({ ...p, [lineKey(it)]: e.target.value }))}
                      placeholder={t("cart.rublePrice")}
                      style={{ width: 96, background: T.bg, color: T.text,
                               border: `1px solid ${T.border}`, padding: "5px 8px",
                               borderRadius: 8, fontSize: 12.5, fontFamily: T_NEW.fontMono }}
                    />
                    {resellerRate && rprices[lineKey(it)] === undefined && (
                      <span style={{ fontSize: 11, color: T_NEW.ink56, fontFamily: T_NEW.fontSans }}>
                        {t("cart.fxSuggested", { rate: resellerRate })}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                <QtyInput value={it.qty}
                  onCommit={(n) => cartSetQty(it.postId, it.variant, n)} />
                <button type="button" onClick={() => cartRemove(it.postId, it.variant)}
                        style={{ background: "none", border: "none", cursor: "pointer",
                                 color: T_NEW.danger, fontSize: T_NEW.micro,
                                 fontFamily: T_NEW.fontMono, textTransform: "uppercase",
                                 letterSpacing: T_NEW.monoTrack }}>
                  {t("cart.remove")}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reseller-CRM attribution */}
      {myCustomers !== null && myCustomers.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Mono style={{ display: "block", marginBottom: 6 }}>{t("order.modal.forCustomer")}</Mono>
          <select value={resellerCustomerId} onChange={(e) => setResellerCustomerId(e.target.value)}
            style={{ width: "100%", background: T.bg, color: T.text, border: `1px solid ${T.border}`,
                     padding: "12px 14px", borderRadius: 12, fontSize: 15, fontFamily: "inherit" }}>
            <option value="">{t("order.modal.forMe")}</option>
            {myCustomers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.telegramHandle ? ` (${c.telegramHandle})` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, color: T.textSec, marginBottom: 6, fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: 0.6 }}>
          {t("order.notes")}
        </div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder={t("order.notes.placeholder")}
          style={{ width: "100%", background: T.bg, color: T.text, border: `1px solid ${T.border}`,
                   padding: "10px 12px", borderRadius: 10, fontSize: 14, minHeight: 56,
                   resize: "vertical", fontFamily: "inherit" }} />
      </div>

      {/* Address block (mirrors OrderModal) */}
      <div style={{ background: T_NEW.paperSoft, padding: 14, borderRadius: T_NEW.r2, marginBottom: T_NEW.s4 }}>
        <Mono color={T_NEW.ink} style={{ display: "block", marginBottom: 10 }}>
          {t("order.address.source.label")}
        </Mono>
        <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
          <button type="button" style={tabBtn(addrSource === "my")} onClick={switchToMy}>
            {t("order.address.source.my")}
          </button>
          <button type="button" style={{ ...tabBtn(addrSource === "customer"),
            opacity: customerTabDisabled ? 0.4 : 1,
            cursor: customerTabDisabled ? "not-allowed" : "pointer" }}
            disabled={customerTabDisabled}
            onClick={() => !customerTabDisabled && switchToCustomer()}>
            {t("order.address.source.customer")}
          </button>
          <button type="button" style={tabBtn(addrSource === "manual")} onClick={switchToManual}>
            {t("order.address.source.manual")}
          </button>
        </div>

        {addrSource === "my" && (
          <AddressList rows={myAddresses} selectedId={selectedAddrId}
            onSelect={(a) => { setSelectedAddrId(a.id); applyAddress(a); }}
            badgeKey="isDefault" badgeLabel={t("order.address.picker.default")}
            emptyText={t("order.address.empty.my")} />
        )}
        {addrSource === "customer" && (resellerCustomerId ? (
          <AddressList rows={customerAddresses} selectedId={selectedAddrId}
            onSelect={(a) => { setSelectedAddrId(a.id); applyAddress(a); }}
            badgeKey="isPrimary" badgeLabel={t("order.address.picker.primary")}
            emptyText={t("order.address.empty.customer")} />
        ) : (
          <Hint text={t("order.address.empty.noCustomer")} />
        ))}
        {addrSource === "manual" && (
          <>
            <Inp label={t("order.recipient.name") + " *"} value={recipientName}
                 onChange={setRecipientName} placeholder={t("order.recipient.name.placeholder")} />
            <Inp label={t("order.recipient.phone")} value={recipientPhone}
                 onChange={setRecipientPhone} placeholder={t("order.modal.phonePlaceholder")} />
            <div style={{ marginBottom: 14 }}>
              <Mono style={{ display: "block", marginBottom: 6 }}>{t("order.address")} *</Mono>
              <textarea value={shipAddress} onChange={(e) => setShipAddress(e.target.value)}
                placeholder={t("order.address.placeholder")}
                style={{ width: "100%", background: T.sidebar, color: T.text,
                         border: `1px solid ${T.border}`, padding: "10px 12px",
                         borderRadius: 10, fontSize: 14, minHeight: 64,
                         resize: "vertical", fontFamily: "inherit" }} />
            </div>
            <Inp label={t("order.country")} value={shipCountry} onChange={setShipCountry}
                 placeholder={t("order.modal.countryDefault")} />
          </>
        )}
      </div>

      {/* Facilitation */}
      <div style={{ marginBottom: T_NEW.s4 }}>
        <Mono size={T_NEW.micro} color={T_NEW.ink56} style={{ display: "block", marginBottom: 6 }}>
          {t("order.facilitate.title")}
        </Mono>
        <div style={{ display: "flex", gap: 8 }}>
          {[[true, t("order.facilitate.byUs")], [false, t("order.facilitate.self")]].map(([val, label]) => (
            <button key={String(val)} type="button" onClick={() => setFacilitated(val)}
              style={{ flex: 1, padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                       border: `1px solid ${facilitated === val ? T_NEW.persimmon : T_NEW.ink12}`,
                       background: facilitated === val ? T_NEW.persimmonSoft : "transparent",
                       color: T_NEW.ink, fontSize: T_NEW.meta, fontFamily: T_NEW.fontSans }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Combined total */}
      <div style={{ background: T_NEW.paperSoft, padding: "12px 16px", borderRadius: T_NEW.r2, marginBottom: T_NEW.s5 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: T_NEW.meta,
                      color: T_NEW.ink56, marginBottom: totals.hasFee ? 6 : 0, fontFamily: T_NEW.fontSans }}>
          <span>{t("cart.subtotalN", { n: items.length })}</span>
          <span style={{ fontFamily: T_NEW.fontMono }}>
            ${totals.subUsd.toFixed(2)}{totals.subRub > 0 ? ` · ₽${totals.subRub.toFixed(0)}` : ""}
          </span>
        </div>
        {totals.hasFee && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: T_NEW.meta,
                        color: T_NEW.ink56, marginBottom: 6, fontFamily: T_NEW.fontSans }}>
            <span>{t("cart.fee")}</span>
            <span style={{ fontFamily: T_NEW.fontMono }}>
              ${totals.feeUsd.toFixed(2)}{totals.feeRub ? ` · ₽${totals.feeRub.toFixed(0)}` : ""}
            </span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                      paddingTop: totals.hasFee ? 10 : 0,
                      borderTop: totals.hasFee ? `1px solid ${T_NEW.ink12}` : "none" }}>
          <Mono color={T_NEW.ink} style={{ fontWeight: 600 }}>{t("order.total")}</Mono>
          <span style={{ textAlign: "right" }}>
            <Serif size={T_NEW.displayMd} color={T_NEW.ink} style={{ display: "inline-block", lineHeight: 1 }}>
              ${(facilitated ? totals.grandUsd : totals.subUsd).toFixed(2)}
            </Serif>
            {(facilitated ? totals.grandRub : totals.subRub) > 0 && (
              <span style={{ display: "block", fontSize: T_NEW.meta, color: T_NEW.ink56,
                             fontFamily: T_NEW.fontMono, marginTop: 4 }}>
                ≈ ₽{(facilitated ? totals.grandRub : totals.subRub).toFixed(0)}
              </span>
            )}
          </span>
        </div>
        {!facilitated && (
          <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, marginTop: 8,
                        fontFamily: T_NEW.fontSans, fontStyle: "italic" }}>
            {t("order.facilitate.selfNote")}
          </div>
        )}
        {facilitated && totals.hasCash && (
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: T_NEW.meta,
                        color: T_NEW.bottle, marginTop: 10, paddingTop: 10,
                        borderTop: `1px dashed ${T_NEW.ink12}`, fontWeight: 600,
                        fontFamily: T_NEW.fontSans }}>
            <span>{t("cart.cashback")}</span>
            <span style={{ fontFamily: T_NEW.fontMono }}>
              +${totals.cashUsd.toFixed(2)}{totals.cashRub ? ` · ₽${totals.cashRub.toFixed(0)}` : ""}
            </span>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose}>{t("common.cancel")}</Btn>
        <Btn disabled={submitting} onClick={submit}>
          {submitting ? t("order.placing") : t("cart.checkout")}
        </Btn>
      </div>
    </Modal>
  );
}
